/**
 * Detecta a bandeira do cartão a partir do número (BIN ranges).
 * Cobre as principais bandeiras do mercado brasileiro.
 */

type BrandRule = {
  brand: string;
  prefixes: number[];
  ranges?: [number, number][];
  lengths: number[];
};

// Cópia das faixas de `detectBrandFromBin` do card-vault (fonte e ordem explicadas lá): o SDK roda
// no browser e não importa o módulo. `tests/detectBrand.test.ts` roda as duas contra a mesma tabela.
const BRAND_RULES: BrandRule[] = [
  {
    brand: 'elo',
    prefixes: [
      401178, 401179, 431274, 438935, 451416, 457393, 457631, 457632, 504175, 627780, 636297,
      636368,
    ],
    ranges: [
      [506699, 506778],
      [509000, 509999],
      [650031, 650033],
      [650035, 650051],
      [650057, 650081],
      [650405, 650439],
      [650485, 650538],
      [650541, 650598],
      [650700, 650718],
      [650720, 650727],
      [650901, 650978],
      [651652, 651679],
      [651680, 651704],
      [655000, 655019],
      [655021, 655058],
    ],
    lengths: [16],
  },
  {
    brand: 'hipercard',
    prefixes: [606282, 637095, 637568, 637599, 637609, 637612],
    lengths: [16],
  },
  {
    brand: 'amex',
    prefixes: [34, 37],
    lengths: [15],
  },
  {
    brand: 'diners',
    prefixes: [36, 38, 39],
    ranges: [[300, 305]],
    lengths: [14, 16],
  },
  {
    brand: 'discover',
    prefixes: [6011, 65],
    ranges: [[644, 649]],
    lengths: [16],
  },
  {
    brand: 'jcb',
    prefixes: [2131, 1800],
    ranges: [[3528, 3589]],
    lengths: [16],
  },
  {
    brand: 'unionpay',
    prefixes: [620, 622018, 6270, 6272, 6276, 6291, 6292, 810],
    ranges: [
      [62100, 62182],
      [62184, 62187],
      [62185, 62197],
      [62200, 62205],
      [622010, 622999],
      [62207, 62209],
      [623, 626],
      [627700, 627779],
      [627781, 627799],
      [6282, 6289],
      [8110, 8131],
      [8132, 8151],
      [8152, 8163],
      [8164, 8171],
    ],
    lengths: [14, 15, 16, 17, 18, 19],
  },
  {
    brand: 'mastercard',
    prefixes: [],
    ranges: [
      [51, 55],
      [2221, 2720],
    ],
    lengths: [16],
  },
  {
    brand: 'visa',
    prefixes: [4],
    lengths: [13, 16, 19],
  },
];

export function detectBrand(cardNumber: string): string {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return '';

  for (const rule of BRAND_RULES) {
    // Check exact prefixes
    for (const prefix of rule.prefixes) {
      const prefixStr = String(prefix);
      if (digits.startsWith(prefixStr) && rule.lengths.includes(digits.length)) {
        return rule.brand;
      }
    }

    // Check ranges
    if (rule.ranges) {
      for (const [start, end] of rule.ranges) {
        const prefixLen = String(start).length;
        const cardPrefix = Number.parseInt(digits.substring(0, prefixLen), 10);
        if (cardPrefix >= start && cardPrefix <= end && rule.lengths.includes(digits.length)) {
          return rule.brand;
        }
      }
    }
  }

  return '';
}

/**
 * Detecta a bandeira a partir dos primeiros dígitos (BIN).
 * Usado quando só temos firstDigits (6 dígitos) ao invés do número completo.
 */
export function detectBrandFromBin(firstDigits: string): string {
  const digits = firstDigits.replace(/\D/g, '');
  if (digits.length < 4) return '';

  for (const rule of BRAND_RULES) {
    for (const prefix of rule.prefixes) {
      const prefixStr = String(prefix);
      if (digits.startsWith(prefixStr) || prefixStr.startsWith(digits)) {
        return rule.brand;
      }
    }

    if (rule.ranges) {
      for (const [start, end] of rule.ranges) {
        const prefixLen = String(start).length;
        if (digits.length >= prefixLen) {
          const cardPrefix = Number.parseInt(digits.substring(0, prefixLen), 10);
          if (cardPrefix >= start && cardPrefix <= end) {
            return rule.brand;
          }
        }
      }
    }
  }

  return '';
}
